/**
 * WorkspaceSession Context
 *
 * Manages workspace sessions - saved configurations of agent views and chat channels.
 * - Uses TanStack Query for data fetching and caching
 * - Maintains activeSession state and auto-save logic
 * - Session switching with pending changes handling
 *
 * @module contexts/WorkspaceSessionContext
 */

import { createContext, useContext, useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  useWorkspaceSessions,
  useCreateSession as useCreateSessionMutation,
  useUpdateSession as useUpdateSessionMutation,
  useDeleteSession as useDeleteSessionMutation,
  useSetDefaultSession as useSetDefaultSessionMutation,
  useCreateSessionFromProject as useCreateSessionFromProjectMutation,
  WORKSPACE_SESSIONS_QUERY_KEY,
} from '../hooks/useWorkspaceSessions.js';
import useUIStore from '../stores/uiStore.js';

const API_BASE = '/api/workspace-sessions';
const AUTO_SAVE_DELAY = 2000; // 2 seconds debounce

const WorkspaceSessionContext = createContext(null);

export function WorkspaceSessionProvider({ children }) {
  const queryClient = useQueryClient();

  // UI Store for persisting last active session ID
  const lastWorkspaceSessionId = useUIStore((state) => state.lastWorkspaceSessionId);
  const setLastWorkspaceSessionId = useUIStore((state) => state.setLastWorkspaceSessionId);

  // TanStack Query for fetching sessions
  const {
    data: sessions = [],
    isLoading: loading,
    error: queryError,
  } = useWorkspaceSessions();

  // Mutations
  const createMutation = useCreateSessionMutation();
  const updateMutation = useUpdateSessionMutation();
  const deleteMutation = useDeleteSessionMutation();
  const setDefaultMutation = useSetDefaultSessionMutation();
  const createFromProjectMutation = useCreateSessionFromProjectMutation();

  // Currently active session (local state - not part of TanStack Query)
  const [activeSession, setActiveSession] = useState(null);

  // Error state (combines query errors and mutation errors)
  const [error, setError] = useState(null);

  // Auto-save pending changes
  const pendingChangesRef = useRef(null);
  const autoSaveTimeoutRef = useRef(null);

  // Ref to avoid stale closure in switchSession
  const activeSessionRef = useRef(activeSession);

  // Track if we've set the initial active session
  const initializedRef = useRef(false);

  // Keep activeSessionRef in sync with activeSession state
  // Also persist to UI store for page reload recovery
  useEffect(() => {
    activeSessionRef.current = activeSession;
    if (activeSession?.id) {
      setLastWorkspaceSessionId(activeSession.id);
    }
  }, [activeSession, setLastWorkspaceSessionId]);

  // Set initial active session when sessions are loaded
  useEffect(() => {
    if (initializedRef.current || loading || sessions.length === 0) return;

    // Try to restore last active session from UI store
    let sessionToActivate = null;

    if (lastWorkspaceSessionId) {
      sessionToActivate = sessions.find(s => s.id === lastWorkspaceSessionId);
    }

    // Fall back to default session if last active not found
    if (!sessionToActivate) {
      sessionToActivate = sessions.find(s => s.isDefault);
    }

    // Fall back to first session if no default
    if (!sessionToActivate) {
      sessionToActivate = sessions[0];
    }

    if (sessionToActivate) {
      setActiveSession(sessionToActivate);
      initializedRef.current = true;
    }
  }, [sessions, loading, lastWorkspaceSessionId]);

  // Keep activeSession in sync with sessions data (when session is updated externally)
  useEffect(() => {
    if (!activeSession || sessions.length === 0) return;

    const updatedSession = sessions.find(s => s.id === activeSession.id);
    if (updatedSession && JSON.stringify(updatedSession) !== JSON.stringify(activeSession)) {
      setActiveSession(updatedSession);
    }
  }, [sessions, activeSession]);

  // Sync query errors to error state
  useEffect(() => {
    if (queryError) {
      setError(queryError.message);
    }
  }, [queryError]);

  // Create new session
  const createSession = useCallback(async (sessionData) => {
    try {
      setError(null);
      const newSession = await createMutation.mutateAsync(sessionData);
      return newSession;
    } catch (err) {
      console.error('[WorkspaceSession] Create failed:', err);
      setError(err.message);
      throw err;
    }
  }, [createMutation]);

  // Update session
  const updateSession = useCallback(async (id, updates) => {
    try {
      setError(null);
      const updatedSession = await updateMutation.mutateAsync({ id, ...updates });

      // Update active session if it's the one being updated
      if (activeSession?.id === id) {
        setActiveSession(updatedSession);
      }

      return updatedSession;
    } catch (err) {
      console.error('[WorkspaceSession] Update failed:', err);
      setError(err.message);
      throw err;
    }
  }, [updateMutation, activeSession]);

  // Delete session
  const deleteSession = useCallback(async (id) => {
    try {
      setError(null);
      await deleteMutation.mutateAsync(id);

      // Clear active session if it was deleted
      if (activeSession?.id === id) {
        setActiveSession(null);
      }
    } catch (err) {
      console.error('[WorkspaceSession] Delete failed:', err);
      setError(err.message);
      throw err;
    }
  }, [deleteMutation, activeSession]);

  // Set session as default
  const setDefaultSession = useCallback(async (id) => {
    try {
      setError(null);
      const updatedSession = await setDefaultMutation.mutateAsync(id);
      return updatedSession;
    } catch (err) {
      console.error('[WorkspaceSession] Set default failed:', err);
      setError(err.message);
      throw err;
    }
  }, [setDefaultMutation]);

  // Create session from project
  const createFromProject = useCallback(async (projectId) => {
    try {
      setError(null);
      const newSession = await createFromProjectMutation.mutateAsync(projectId);
      return newSession;
    } catch (err) {
      console.error('[WorkspaceSession] Create from project failed:', err);
      setError(err.message);
      throw err;
    }
  }, [createFromProjectMutation]);

  // Switch to a session
  // Uses refs to avoid stale closure - this callback should be stable
  const switchSession = useCallback((session) => {
    // Cancel any pending auto-save for old session
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
      autoSaveTimeoutRef.current = null;
    }

    // Save pending changes before switching (use ref for current value)
    const currentSession = activeSessionRef.current;
    if (pendingChangesRef.current && currentSession) {
      // Fire-and-forget save of pending changes
      fetch(`${API_BASE}/${currentSession.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(pendingChangesRef.current),
      })
        .then(() => {
          // Invalidate cache after saving
          queryClient.invalidateQueries({ queryKey: WORKSPACE_SESSIONS_QUERY_KEY });
        })
        .catch(console.error);
      pendingChangesRef.current = null;
    }

    setActiveSession(session);
    // Note: lastWorkspaceSessionId is automatically updated via useEffect when activeSession changes
  }, [queryClient]); // Only queryClient dependency - uses refs for current values

  // Queue auto-save for current session
  const queueAutoSave = useCallback((changes) => {
    if (!activeSession) return;

    // Merge with existing pending changes
    pendingChangesRef.current = {
      ...pendingChangesRef.current,
      ...changes,
    };

    // Clear existing timeout
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
    }

    // Set new timeout
    autoSaveTimeoutRef.current = setTimeout(() => {
      if (pendingChangesRef.current && activeSession) {
        updateSession(activeSession.id, pendingChangesRef.current)
          .then(() => {
            pendingChangesRef.current = null;
          })
          .catch(console.error);
      }
    }, AUTO_SAVE_DELAY);
  }, [activeSession, updateSession]);

  // Refetch sessions (for manual refresh)
  const fetchSessions = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: WORKSPACE_SESSIONS_QUERY_KEY });
  }, [queryClient]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
    };
  }, []);

  const value = useMemo(() => ({
    // State
    sessions,
    activeSession,
    loading,
    error,

    // Actions
    fetchSessions,
    createSession,
    updateSession,
    deleteSession,
    setDefaultSession,
    createFromProject,
    switchSession,
    queueAutoSave,
  }), [
    sessions,
    activeSession,
    loading,
    error,
    fetchSessions,
    createSession,
    updateSession,
    deleteSession,
    setDefaultSession,
    createFromProject,
    switchSession,
    queueAutoSave,
  ]);

  return (
    <WorkspaceSessionContext.Provider value={value}>
      {children}
    </WorkspaceSessionContext.Provider>
  );
}

export function useWorkspaceSession() {
  const context = useContext(WorkspaceSessionContext);
  if (!context) {
    throw new Error('useWorkspaceSession must be used within WorkspaceSessionProvider');
  }
  return context;
}

export default WorkspaceSessionContext;
